// apps/web/src/pages/Visitors.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Button,
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Alert,
  AlertColor,
  Paper,
  InputAdornment,
  TextField,
  Chip,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Stack,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Collapse,
  Fab,
} from '@mui/material';
import {
  Add,
  QrCodeScanner,
  Close,
  Search,
  Refresh,
  ViewList,
  GridView,
  FitnessCenter as FitnessCenterIcon,
  Today as TodayIcon,
  CardMembership as CardMembershipIcon,
  InfoOutlined,
  FilterAlt,
  Clear,
} from '@mui/icons-material';

import VisitorForm from '../components/visitors/VisitorForm';
import VisitorCard from '../components/visitors/VisitorCard';
import VisitorQRDialog from '../components/visitors/VisitorCard/VisitorQRDialog';
import QRCodeScannerDialog from '../components/visitors/QRCodeScannerDialog';
import SubscriptionManagementDialog from '../components/subscriptions/SubscriptionManagementDialog';
import { PaginationBar } from '../components/common/PaginationBar';
import { apiClient } from '../api/client';
import {
  Visitor,
  VisitorWithSubscription,
  CreateVisitorRequest,
  UpdateVisitorRequest,
  ActiveSubscriptionInfo,
} from '@gym-management/shared';

const TEXTS = {
  pageTitle: 'Управление посетителями',
  pageSubtitle:
    'Управляйте посетителями тренажерного зала, отслеживайте абонементы и регистрируйте посещения',
  stats: {
    currentlyInside: 'Сейчас в зале',
    todayVisits: 'Посещений сегодня',
    memberships: 'Абонементы',
  },
  searchPlaceholder: 'Поиск посетителей...',
  filters: {
    all: 'Все',
    withSubscription: 'С абонементом',
    withoutSubscription: 'Без абонемента',
    checkedIn: 'В зале',
    hideInactive: 'Скрыть неактивных',
    inactiveTooltip:
      'Неактивные – посетители без активного абонемента и без посещений за последние 90 дней',
  },
  actions: {
    refresh: 'Обновить',
    scanQr: 'Сканировать QR',
    addVisitor: 'Добавить посетителя',
    showHistory: 'История посещений',
  },
  viewModes: {
    list: 'Список',
    grid: 'Плитка',
  },
  notifications: {
    visitorCreated: (name: string) => `Посетитель "${name}" успешно создан`,
    visitorUpdated: 'Посетитель обновлён',
    visitorDeleted: 'Посетитель удалён',
    deleteConfirm: 'Вы уверены, что хотите удалить этого посетителя?',
    checkInSuccess: (name: string) => `${name} успешно зарегистрирован`,
    checkOutSuccess: (name: string) => `${name} отмечен как вышедший`,
    errorLoading: 'Ошибка загрузки посетителей',
    errorCreating: 'Ошибка создания посетителя',
    errorUpdating: 'Ошибка обновления посетителя',
    errorDeleting: 'Ошибка удаления посетителя',
    errorCheckIn: 'Ошибка при регистрации',
    errorCheckOut: 'Ошибка при отметке выхода',
  },
  emptyStates: {
    noMatch: 'Нет посетителей по вашему запросу',
    noVisitors: 'Посетители не найдены',
  },
} as const;

export default function Visitors() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ---------------------- Состояния ----------------------
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [filterHasSubscription, setFilterHasSubscription] = useState<boolean | null>(null);
  const [filterCheckedIn, setFilterCheckedIn] = useState<boolean | null>(null);

  const [visitors, setVisitors] = useState<Visitor[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [activeMembersCount, setActiveMembersCount] = useState(0);
  const [todayVisitsCount, setTodayVisitsCount] = useState(0);
  const [activeVisitsCount, setActiveVisitsCount] = useState(0);
  const [activeVisits, setActiveVisits] = useState<Record<string, boolean>>({});
  const [activeSubscriptions, setActiveSubscriptions] = useState<
    Record<string, ActiveSubscriptionInfo>
  >({});
  const [loadingActiveVisits, setLoadingActiveVisits] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [selectedVisitorWithSub, setSelectedVisitorWithSub] =
    useState<VisitorWithSubscription | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const [scannerOpen, setScannerOpen] = useState(false);
  const [visitorDetailOpen, setVisitorDetailOpen] = useState(false);
  const [qrDialogVisitor, setQrDialogVisitor] = useState<Visitor | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedVisitorForSubscription, setSelectedVisitorForSubscription] = useState<{
    visitorId: string;
    subscription: ActiveSubscriptionInfo;
  } | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Для мобильного сворачивания фильтров
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const initializedRef = useRef(false);

  // ==================== УВЕДОМЛЕНИЯ ====================
  const showNotification = useCallback((message: string, severity: AlertColor = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const showError = useCallback(
    (message: string) => {
      showNotification(message, 'error');
    },
    [showNotification]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // ==================== ЗАГРУЗКА ДАННЫХ ====================

  const loadActiveVisits = useCallback(async () => {
    setLoadingActiveVisits(true);
    try {
      const data = await apiClient.visits.getActiveVisits(1, 100);
      const activeMap: Record<string, boolean> = {};
      (data.visits || []).forEach((visit) => {
        if (visit?.check_out) return;
        const visitorId = visit?.visitor_id;
        if (visitorId) activeMap[String(visitorId)] = true;
      });
      setActiveVisits(activeMap);
    } catch (error) {
      console.error('Failed to load active visits:', error);
      setActiveVisits({});
    } finally {
      setLoadingActiveVisits(false);
    }
  }, []);

  const loadActiveSubscriptions = useCallback(async (currentVisitors?: Visitor[]) => {
    try {
      const subscriptions = await apiClient.getAllActiveSubscriptions();
      const map: Record<string, ActiveSubscriptionInfo> = {};
      currentVisitors?.forEach((v) => {
        const sub = subscriptions.find((s) => s.visitorId === v.id);
        if (sub) map[v.id] = sub;
      });
      setActiveSubscriptions(map);
    } catch (error) {
      console.error('Failed to load active subscriptions:', error);
      setActiveSubscriptions({});
    }
  }, []);

  const loadActiveMembersCount = useCallback(async () => {
    try {
      const subscriptions = await apiClient.getAllActiveSubscriptions();
      const uniqueVisitors = new Set(subscriptions.map((sub) => sub.visitorId));
      setActiveMembersCount(uniqueVisitors.size);
    } catch (error) {
      console.error('Failed to load active members count:', error);
      setActiveMembersCount(0);
    }
  }, []);

  const fetchTodayVisits = useCallback(async () => {
    try {
      const stats = await apiClient.visits.getDailyStats();
      setTodayVisitsCount(stats.total_visits ?? 0);
      setActiveVisitsCount(stats.active_visits ?? 0);
    } catch (error) {
      console.error('Failed to fetch today visits:', error);
      setTodayVisitsCount(0);
      setActiveVisitsCount(0);
    }
  }, []);

  const fetchVisitors = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.visitors.getVisitors(
        page,
        limit,
        searchQuery,
        filterActive,
        filterCheckedIn,
        filterHasSubscription
      );
      setVisitors(response.visitors || []);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || Math.ceil(response.total / limit));
    } catch (error) {
      showError(TEXTS.notifications.errorLoading);
      console.error(error);
      setVisitors([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, filterActive, filterCheckedIn, filterHasSubscription, showError]);

  // ==================== ОБНОВЛЕНИЕ ДАННЫХ ====================

  const refreshAllData = useCallback(async () => {
    await fetchVisitors();
    fetchTodayVisits();
    loadActiveVisits();
    loadActiveMembersCount();
  }, [fetchVisitors, fetchTodayVisits, loadActiveVisits, loadActiveMembersCount]);

  // ==================== ЭФФЕКТЫ ====================

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([
        fetchVisitors(),
        fetchTodayVisits(),
        loadActiveVisits(),
        loadActiveMembersCount(),
      ]);
      initializedRef.current = true;
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (visitors && visitors.length > 0) {
      loadActiveSubscriptions(visitors);
      loadActiveMembersCount();
    }
  }, [visitors, loadActiveSubscriptions, loadActiveMembersCount]);

  useEffect(() => {
    if (!initializedRef.current) return;
    fetchVisitors();
  }, [page, fetchVisitors]);

  // Debounce сброса страницы при изменении фильтров
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, filterActive, filterHasSubscription, filterCheckedIn]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadActiveVisits();
      fetchTodayVisits();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== МУТАЦИИ ====================

  const resetToFirstPage = useCallback(() => {
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (data: CreateVisitorRequest) => {
      try {
        const newVisitor = await apiClient.visitors.createVisitor(data);
        showNotification(TEXTS.notifications.visitorCreated(newVisitor.name), 'success');
        setSelectedVisitor(newVisitor);
        setSelectedVisitorWithSub(null);
        setFormOpen(false);
        setVisitorDetailOpen(true);
        resetToFirstPage();
        await refreshAllData();
      } catch (error) {
        showError(TEXTS.notifications.errorCreating);
        console.error('Failed to create visitor:', error);
      }
    },
    [showNotification, showError, resetToFirstPage, refreshAllData]
  );

  const handleUpdate = useCallback(
    async (data: UpdateVisitorRequest) => {
      if (!selectedVisitorWithSub) return;
      try {
        if (Object.keys(data).length > 0) {
          const updatedVisitor = await apiClient.visitors.updateVisitor(
            selectedVisitorWithSub.id,
            data
          );
          setSelectedVisitor(updatedVisitor || null);
        }

        const subscriptions = await apiClient.getAllActiveSubscriptions();
        const newSub = subscriptions.find((s) => s.visitorId === selectedVisitorWithSub.id);
        setActiveSubscriptions((prev) => {
          const next = { ...prev };
          if (newSub) next[selectedVisitorWithSub.id] = newSub;
          else delete next[selectedVisitorWithSub.id];
          return next;
        });

        const refreshedVisitor = await apiClient.getVisitor(selectedVisitorWithSub.id);
        setSelectedVisitorWithSub(refreshedVisitor);
        setSelectedVisitor(refreshedVisitor);

        showNotification(TEXTS.notifications.visitorUpdated, 'success');
        setFormOpen(false);
        resetToFirstPage();
        await refreshAllData();
      } catch (error) {
        showError(TEXTS.notifications.errorUpdating);
        console.error('Failed to update visitor:', error);
      }
    },
    [selectedVisitorWithSub, showNotification, showError, resetToFirstPage, refreshAllData]
  );

  const handleFormSubmit = useCallback(
    async (data: CreateVisitorRequest | UpdateVisitorRequest) => {
      if (selectedVisitorWithSub) {
        await handleUpdate(data as UpdateVisitorRequest);
      } else {
        await handleCreate(data as CreateVisitorRequest);
      }
    },
    [selectedVisitorWithSub, handleCreate, handleUpdate]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm(TEXTS.notifications.deleteConfirm)) return;
      try {
        await apiClient.visitors.deleteVisitor(id);
        showNotification(TEXTS.notifications.visitorDeleted, 'success');
        setVisitorDetailOpen(false);
        setSelectedVisitor(null);
        setSelectedVisitorWithSub(null);
        resetToFirstPage();
        await refreshAllData();
      } catch (error) {
        showError(TEXTS.notifications.errorDeleting);
        console.error('Failed to delete visitor:', error);
      }
    },
    [showNotification, showError, resetToFirstPage, refreshAllData]
  );

  // ==================== ОБРАБОТЧИКИ ДЕЙСТВИЙ ====================

  const checkVisitorStatus = useCallback(
    (visitorId: string): boolean => !!activeVisits[visitorId],
    [activeVisits]
  );

  const updateVisitorStatus = useCallback((visitorId: string, isCheckedIn: boolean) => {
    setActiveVisits((prev) => ({ ...prev, [visitorId]: isCheckedIn }));
  }, []);

  const handleEditVisitor = useCallback(
    async (visitor: Visitor) => {
      try {
        const visitorWithSub = await apiClient.getVisitor(visitor.id);
        setSelectedVisitor(visitorWithSub);
        setSelectedVisitorWithSub(visitorWithSub);
        setFormOpen(true);
      } catch (error) {
        console.error('Failed to load visitor details:', error);
        showError('Не удалось загрузить данные посетителя');
      }
    },
    [showError]
  );

  const handleViewDetails = useCallback((visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setVisitorDetailOpen(true);
  }, []);

  const handleShowQRDialog = useCallback((visitor: Visitor) => {
    setQrDialogVisitor(visitor);
    setQrDialogOpen(true);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'list' | 'grid'
  ) => {
    if (newMode !== null) setViewMode(newMode);
  };

  const handleShowHistory = useCallback(
    (visitor: Visitor) => {
      navigate('/checkins', { state: { visitorId: visitor.id, visitorName: visitor.name } });
    },
    [navigate]
  );

  const handleCheckIn = useCallback(
    async (visitor: Visitor) => {
      try {
        await apiClient.visits.checkIn(visitor.id);
        showNotification(TEXTS.notifications.checkInSuccess(visitor.name), 'success');
        updateVisitorStatus(visitor.id, true);
        refreshAllData();
      } catch (error) {
        showError(error instanceof Error ? error.message : TEXTS.notifications.errorCheckIn);
      }
    },
    [showNotification, showError, updateVisitorStatus, refreshAllData]
  );

  const handleCheckOut = useCallback(
    async (visitor: Visitor) => {
      try {
        await apiClient.visits.checkOut(undefined, visitor.id);
        showNotification(TEXTS.notifications.checkOutSuccess(visitor.name), 'success');
        updateVisitorStatus(visitor.id, false);
        refreshAllData();
      } catch (error) {
        showError(error instanceof Error ? error.message : TEXTS.notifications.errorCheckOut);
      }
    },
    [showNotification, showError, updateVisitorStatus, refreshAllData]
  );

  const handleManageSubscription = useCallback(
    (visitorId: string, subscription: ActiveSubscriptionInfo) => {
      setSelectedVisitorForSubscription({ visitorId, subscription });
      setSubscriptionDialogOpen(true);
    },
    []
  );

  const handleSubscriptionSuccess = useCallback(async () => {
    setSubscriptionDialogOpen(false);
    if (selectedVisitorWithSub) {
      try {
        const refreshed = await apiClient.getVisitor(selectedVisitorWithSub.id);
        setSelectedVisitor(refreshed);
        setSelectedVisitorWithSub(refreshed);
      } catch (error) {
        console.error('Failed to refresh visitor after subscription update:', error);
      }
    }
    refreshAllData();
    showNotification('Абонемент успешно обновлён', 'success');
  }, [selectedVisitorWithSub, refreshAllData, showNotification]);

  // Сброс всех фильтров (для пустого состояния)
  const handleClearAllFilters = useCallback(() => {
    setSearchQuery('');
    setFilterActive(null);
    setFilterHasSubscription(null);
    setFilterCheckedIn(null);
    setPage(1);
  }, []);

  // ==================== РЕНДЕР ====================

  const effectiveViewMode = isMobile ? 'grid' : viewMode;

  // Скелетон для режима списка
  const renderListSkeleton = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: { xs: '60%', sm: '50%', md: '40%' }, maxWidth: 500 }}>
              Посетитель
            </TableCell>
            <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>Регистрация</TableCell>
            <TableCell align="right" sx={{ width: { xs: 140, sm: 160, md: 180 }, pr: 2 }}>
              Действия
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(limit > 10 ? 10 : limit)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Box display="flex" alignItems="center" gap={2}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={120} />
                    <Skeleton width={80} />
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Skeleton width={80} />
              </TableCell>
              <TableCell align="right">
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Скелетон для режима плитки
  const renderGridSkeleton = () => (
    <Grid container spacing={3}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            <Skeleton width="60%" sx={{ mt: 1 }} />
            <Skeleton width="40%" />
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Заголовок страницы */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
          {TEXTS.pageTitle}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {TEXTS.pageSubtitle}
        </Typography>
      </Box>

      {/* ==================== СТАТИСТИКА (адаптивная) ==================== */}
      {isMobile ? (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-around', mb: 4, px: 1 }}>
          <Tooltip title="Сейчас в зале">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FitnessCenterIcon color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight={600}>
                {activeVisitsCount}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Посещений сегодня">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TodayIcon color="info" fontSize="small" />
              <Typography variant="h6" fontWeight={600}>
                {todayVisitsCount}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Абонементы">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CardMembershipIcon color="success" fontSize="small" />
              <Typography variant="h6" fontWeight={600}>
                {activeMembersCount}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 4,
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={0.5}>
                <FitnessCenterIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                <Typography variant="h4" fontWeight={600} color="primary.main">
                  {activeVisitsCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {TEXTS.stats.currentlyInside}
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={0.5}>
                <TodayIcon sx={{ fontSize: 28, color: 'info.main' }} />
                <Typography variant="h4" fontWeight={600} color="info.main">
                  {todayVisitsCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {TEXTS.stats.todayVisits}
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={0.5}>
                <CardMembershipIcon sx={{ fontSize: 28, color: 'success.main' }} />
                <Typography variant="h4" fontWeight={600} color="success.main">
                  {activeMembersCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {TEXTS.stats.memberships}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ==================== ПАНЕЛЬ ФИЛЬТРОВ И ДЕЙСТВИЙ (адаптивная) ==================== */}
      <Paper sx={{ p: 2, mb: 4 }}>
        {!isMobile ? (
          // Десктопная версия: все фильтры и кнопки в две строки
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder={TEXTS.searchPlaceholder}
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                >
                  <ToggleButton value="list" aria-label={TEXTS.viewModes.list}>
                    <ViewList fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="grid" aria-label={TEXTS.viewModes.grid}>
                    <GridView fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={refreshAllData}
                  disabled={isLoading || loadingActiveVisits}
                  size="small"
                >
                  {TEXTS.actions.refresh}
                </Button>
                <Button
                  variant="contained" // <-- выделяем сканирование
                  color="primary"
                  startIcon={<QrCodeScanner />}
                  onClick={() => setScannerOpen(true)}
                  size="small"
                >
                  {TEXTS.actions.scanQr}
                </Button>
                <Button
                  variant="outlined" // <-- добавление становится второстепенным
                  startIcon={<Add />}
                  onClick={() => {
                    setSelectedVisitor(null);
                    setSelectedVisitorWithSub(null);
                    setFormOpen(true);
                  }}
                  size="small"
                >
                  {TEXTS.actions.addVisitor}
                </Button>
              </Box>
            </Box>

            <Divider />

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={TEXTS.filters.all}
                  onClick={() => setFilterHasSubscription(null)}
                  color={filterHasSubscription === null ? 'primary' : 'default'}
                  size="small"
                />
                <Chip
                  label={TEXTS.filters.withSubscription}
                  onClick={() => setFilterHasSubscription(true)}
                  color={filterHasSubscription === true ? 'primary' : 'default'}
                  size="small"
                />
                <Chip
                  label={TEXTS.filters.withoutSubscription}
                  onClick={() => setFilterHasSubscription(false)}
                  color={filterHasSubscription === false ? 'primary' : 'default'}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filterCheckedIn === true}
                      onChange={(e) => setFilterCheckedIn(e.target.checked ? true : null)}
                      size="small"
                    />
                  }
                  label={TEXTS.filters.checkedIn}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filterActive === true}
                        onChange={(e) => setFilterActive(e.target.checked ? true : null)}
                        size="small"
                      />
                    }
                    label={TEXTS.filters.hideInactive}
                  />
                  <Tooltip title={TEXTS.filters.inactiveTooltip} arrow placement="top">
                    <IconButton size="small" sx={{ ml: -0.5 }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Chip label={`Всего записей: ${total}`} variant="outlined" />
            </Box>
          </Stack>
        ) : (
          // Мобильная версия
          <>
            {/* Строка поиска на всю ширину */}
            <Box sx={{ mb: 2 }}>
              <TextField
                placeholder={TEXTS.searchPlaceholder}
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Строка дополнительных действий (без сканирования) */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="list" aria-label={TEXTS.viewModes.list}>
                  <ViewList fontSize="small" />
                </ToggleButton>
                <ToggleButton value="grid" aria-label={TEXTS.viewModes.grid}>
                  <GridView fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={refreshAllData}
                disabled={isLoading || loadingActiveVisits}
                size="small"
              >
                Обновить
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setSelectedVisitor(null);
                  setSelectedVisitorWithSub(null);
                  setFormOpen(true);
                }}
                size="small"
              >
                Добавить
              </Button>
            </Box>

            {/* Кнопка "Фильтры" на всю ширину */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAlt />}
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              aria-expanded={filtersExpanded}
              aria-label="Показать фильтры"
              fullWidth
              sx={{ mb: filtersExpanded ? 2 : 0 }}
            >
              Фильтры
            </Button>

            {/* Раскрывающаяся панель с фильтрами (без заголовка) */}
            <Collapse in={filtersExpanded}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={TEXTS.filters.all}
                    onClick={() => setFilterHasSubscription(null)}
                    color={filterHasSubscription === null ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={TEXTS.filters.withSubscription}
                    onClick={() => setFilterHasSubscription(true)}
                    color={filterHasSubscription === true ? 'primary' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={TEXTS.filters.withoutSubscription}
                    onClick={() => setFilterHasSubscription(false)}
                    color={filterHasSubscription === false ? 'primary' : 'default'}
                    size="small"
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filterCheckedIn === true}
                      onChange={(e) => setFilterCheckedIn(e.target.checked ? true : null)}
                      size="small"
                    />
                  }
                  label={TEXTS.filters.checkedIn}
                />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filterActive === true}
                        onChange={(e) => setFilterActive(e.target.checked ? true : null)}
                        size="small"
                      />
                    }
                    label={TEXTS.filters.hideInactive}
                  />
                  <Tooltip title={TEXTS.filters.inactiveTooltip} arrow placement="top">
                    <IconButton size="small">
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Chip label={`Всего: ${total}`} variant="outlined" />
              </Stack>
            </Collapse>
          </>
        )}
      </Paper>

      {/* ==================== ОСНОВНОЙ КОНТЕНТ ==================== */}
      {effectiveViewMode === 'list' ? (
        isLoading ? (
          renderListSkeleton()
        ) : visitors && visitors.length > 0 ? (
          <Paper sx={{ width: '100%', mb: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: { xs: '60%', sm: '50%', md: '40%' }, maxWidth: 500 }}>
                      Посетитель
                    </TableCell>
                    <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>Регистрация</TableCell>
                    <TableCell align="right" sx={{ width: { xs: 140, sm: 160, md: 180 }, pr: 2 }}>
                      Действия
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visitors.map((visitor) => (
                    <VisitorCard
                      key={visitor.id}
                      visitor={visitor}
                      mode="table"
                      onEdit={handleEditVisitor}
                      onDelete={handleDelete}
                      onViewDetails={handleViewDetails}
                      onShowQR={handleShowQRDialog}
                      onShowHistory={handleShowHistory}
                      onCheckIn={() => handleCheckIn(visitor)}
                      onCheckOut={() => handleCheckOut(visitor)}
                      isCheckedIn={checkVisitorStatus(visitor.id)}
                      activeSubscription={activeSubscriptions[visitor.id]}
                      hasActiveSubscription={!!activeSubscriptions[visitor.id]}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <PaginationBar
                count={total}
                page={page}
                rowsPerPage={limit}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Строк"
              />
            )}
          </Paper>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery ||
              filterActive !== null ||
              filterHasSubscription !== null ||
              filterCheckedIn !== null
                ? TEXTS.emptyStates.noMatch
                : TEXTS.emptyStates.noVisitors}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleClearAllFilters}
              sx={{ mt: 2 }}
            >
              Сбросить фильтры
            </Button>
          </Paper>
        )
      ) : isLoading ? (
        renderGridSkeleton()
      ) : visitors && visitors.length > 0 ? (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {visitors.map((visitor) => (
              <Grid item key={visitor.id} xs={12} sm={6} md={4} lg={3}>
                <VisitorCard
                  visitor={visitor}
                  mode="normal"
                  onEdit={handleEditVisitor}
                  onDelete={handleDelete}
                  onViewDetails={handleViewDetails}
                  onShowQR={handleShowQRDialog}
                  onShowHistory={handleShowHistory}
                  onCheckIn={() => handleCheckIn(visitor)}
                  onCheckOut={() => handleCheckOut(visitor)}
                  isCheckedIn={checkVisitorStatus(visitor.id)}
                  activeSubscription={activeSubscriptions[visitor.id]}
                  hasActiveSubscription={!!activeSubscriptions[visitor.id]}
                />
              </Grid>
            ))}
          </Grid>
          {totalPages > 1 && (
            <PaginationBar
              count={total}
              page={page}
              rowsPerPage={limit}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Строк"
            />
          )}
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchQuery ||
            filterActive !== null ||
            filterHasSubscription !== null ||
            filterCheckedIn !== null
              ? TEXTS.emptyStates.noMatch
              : TEXTS.emptyStates.noVisitors}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearAllFilters}
            sx={{ mt: 2 }}
          >
            Сбросить фильтры
          </Button>
        </Paper>
      )}

      {/* Плавающая кнопка сканирования (только на мобильных) */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="scan"
          onClick={() => setScannerOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: theme.zIndex.fab,
          }}
        >
          <QrCodeScanner />
        </Fab>
      )}

      {/* Модальные окна и уведомления */}
      <VisitorForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedVisitor(null);
          setSelectedVisitorWithSub(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedVisitor || undefined}
        activeSubscription={selectedVisitorWithSub?.activeSubscription}
        onManageSubscription={handleManageSubscription}
      />

      <QRCodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onVisitorFound={(visitor) => {
          setSelectedVisitor(visitor);
          setScannerOpen(false);
          setVisitorDetailOpen(true);
        }}
      />

      <Dialog
        open={visitorDetailOpen}
        onClose={() => setVisitorDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              maxHeight: '100vh',
              height: '100vh',
              borderRadius: 0,
            }),
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
            <IconButton
              onClick={() => setVisitorDetailOpen(false)}
              size="small"
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <Close />
            </IconButton>
          </Box>
          {selectedVisitor && (
            <VisitorCard
              visitor={selectedVisitor}
              mode={isMobile ? 'normal' : 'full'}
              onEdit={handleEditVisitor}
              onDelete={handleDelete}
              onViewDetails={handleViewDetails}
              onShowQR={handleShowQRDialog}
              onShowHistory={handleShowHistory}
              onCheckIn={() => handleCheckIn(selectedVisitor)}
              onCheckOut={() => handleCheckOut(selectedVisitor)}
              isCheckedIn={checkVisitorStatus(selectedVisitor.id)}
              hasActiveSubscription={!!activeSubscriptions[selectedVisitor.id]}
              activeSubscription={activeSubscriptions[selectedVisitor.id]}
            />
          )}
        </DialogContent>
      </Dialog>

      <VisitorQRDialog
        key={qrDialogVisitor?.id || 'qr-dialog'}
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        visitorName={qrDialogVisitor?.name || ''}
        qrData={
          qrDialogVisitor
            ? JSON.stringify({
                type: 'gym_visitor',
                id: qrDialogVisitor.id,
                name: qrDialogVisitor.name,
              })
            : ''
        }
      />

      <SubscriptionManagementDialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        visitorId={selectedVisitorForSubscription?.visitorId}
        currentSubscription={selectedVisitorForSubscription?.subscription}
        onSuccess={handleSubscriptionSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
